import useSocketReconnect from '../../utils/useSocketReconnect';
import { useEffect, useRef, useState } from "react";

import ChooseEvent from "./ChooseEvent";
import FormResponses from "./FormResponses";
import FormEditor from "./FormEditor";
import FormLogs from "./FormLogs";

import moment from "moment";
import 'moment/locale/ca';
import { applyTimeZone } from "../../utils/utils"
import { fetchAPI, getSubdomain } from "../../utils/utils";
import UserInfo from "../login/UserInfo";
import Pressable from "../other/Pressable";
import HeaderTabs from "../interface/HeaderTabs";
import { isBrowser } from "react-device-detect";
import FormsStats from "./FormsStats";

const COLLA = getSubdomain();

function Notifications({ msg, setMsg }) {
    const notifications_style = {
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: 50,
      backgroundColor: 'rgb(100, 180, 100)',
      color: 'white',
      display: 'flex',
      justifyContent: 'space-around',
      alignItems: 'center',
      padding: 10,
      zIndex: 1000,
      flexWrap: 'wrap',
    };
    
    const [show, setShow] = useState(false);
  
    useEffect(() => {
      if (!msg) return;
      setShow(true);
    }, [msg]);
    
    useEffect(() => {
      if (show) {
        setTimeout(() => {
          setShow(false);
        }, (msg.timeout || 3000));
      } else {
        setMsg(null);
      }
    }, [show]);
  
      return show && (
          <div style={notifications_style}>
            <div
              style={{
                flexShrink: 0,
              }}
            >
              {msg?.msg}
            </div>
            <div
              style={{
                flexShrink: 0,
              }}
            >
              <a target="_blank" href={msg?.link} style={{ color: 'white' }}>
                {msg?.linkText}
              </a>
            </div>
          </div>
      )
  }  

function FormInfoRow({ socket, eventId, eventInfo, form }) {
  const rowRef = useRef(null);
  // Each row loads its counts once it comes near the screen: loading all 50 rows
  // at once queued 150 requests ahead of whatever was clicked next.
  const [isNearScreen, setIsNearScreen] = useState(false);

    useSocketReconnect(socket, () => {
        if (eventId && isNearScreen) socket.emit('.request_form_responses', eventId);
    });

  const [userIds, setUserIds] = useState([]);
  const [assistencies, setAssistencies] = useState([]);
  const [totalMoney, setTotalMoney] = useState(0);

  useEffect(() => {
    const row = rowRef.current;
    if (!row) return;

    const observer = new IntersectionObserver(entries => {
      if (entries.some(entry => entry.isIntersecting)) {
        setIsNearScreen(true);
        observer.disconnect();
      }
    }, { rootMargin: '300px 0px' });

    observer.observe(row);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (eventId && isNearScreen) {
      fetchAPI(`/assistencies_event/${eventId}`, data => {
        setAssistencies(data.data)
      })
    }
  }, [eventId, isNearScreen])

  useEffect(() => {
    if (!isNearScreen) return;
    fetchAPI(`/get_form_total_money/${eventId}`, data => {
      setTotalMoney(data.total_money)
    })
  }, [eventId, isNearScreen])

  const subgroupOptions = {
      'No confirmat': { color: 'orange', emoji: '😶' },
      'Vinc': { color: 'green', emoji: '✅' },
      'No vinc': { color: 'red', emoji: '❌' }
  }

  const formatDate = date =>
    applyTimeZone(date)
      .locale('ca')
      .format(`dddd, D [${ [3, 7, 9].includes(applyTimeZone(date).toDate().getMonth()) ? 'd\'' : 'de ' }]MMMM`)
      .split('')
      .map((letter, index) => index == 0 ? letter.toUpperCase() : letter)
      .join('')

  useEffect(() => {
    if (!isNearScreen) return;

    const onFormResponses = res => {
      if (res.evId === eventId) setUserIds(Object.keys(res.responses))
    }
    socket.on('.form_responses', onFormResponses)
    socket.emit('.request_form_responses', eventId)

    return () => socket.off('.form_responses', onFormResponses)
  }, [eventId, form, isNearScreen])

  const tdStyle = { padding: '10px 12px', verticalAlign: 'middle' };

  return (
    <tr
      ref={rowRef}
      className="forms-table-row"
      style={{ cursor: 'pointer' }}
      onClick={e => {
        if (e.metaKey || e.ctrlKey) window.open(`/forms/${eventId}`, '_blank');
        else window.location.href = `/forms/${eventId}`;
      }}
    >
      <td style={{ ...tdStyle, maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        <a
          href={`/forms/${eventId}`}
          onClick={e => e.stopPropagation()}
          style={{ color: 'inherit', fontWeight: 'bold', textDecoration: 'underline' }}
        >
          {eventInfo.title}
        </a>
        <div style={{ fontSize: '0.85rem', color: '#555', fontWeight: 300 }}>
          {formatDate(eventInfo['data-esperada-inici'])}
        </div>
      </td>
      <td style={{ ...tdStyle, whiteSpace: 'nowrap' }}>
        {Object.keys(subgroupOptions).map(subgroupName =>
          <span key={subgroupName} style={{ color: subgroupOptions[subgroupName].color, marginRight: 8 }}>
            {subgroupOptions[subgroupName].emoji}&nbsp;
            {assistencies.filter(row => row.assistencia === subgroupName).length}
          </span>
        )}
      </td>
      <td style={{ ...tdStyle, textAlign: 'center' }}>
        {userIds.length}
      </td>
      <td style={{ ...tdStyle, textAlign: 'right', whiteSpace: 'nowrap' }}>
        {totalMoney > 0 ? (
          <>{Math.round(totalMoney).toString().replace(/\B(?=(\d{3})+(?!\d))/g, '.')}€</>
        ) : (
          <span style={{ color: '#999' }}>—</span>
        )}
      </td>
    </tr>
  );
}

function CorreusSection({ socket }) {
    useSocketReconnect(socket, () => {
        socket.emit('.get_default_mails');
    });

  const [mails, setMails] = useState([]);

  useEffect(() => {
    socket.emit('.get_default_mails');

    const onDefaultMails = data => {
      setMails([
        data['owner'],
        ...data['writers'],
        ...data['readers'],
      ].filter(mail => mail));
    };
    socket.on('.default_mails', onDefaultMails);

    return () => {
      socket.off('.default_mails', onDefaultMails);
    };
  }, []);

  const isTextAMail = text => {
    const mailRegex = /^[a-zA-Z0-9._-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,4}$/;
    return mailRegex.test(text);
  };

  const styles = {
    container: {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      gap: '1rem',
    },
    mail: {
      display: 'flex',
      flexDirection: 'row',
      alignItems: 'center',
      justifyContent: 'space-between',
      width: '100%',
      padding: '0.5rem',
      borderRadius: '5px',
      boxShadow: '0 2px 5px rgba(0, 0, 0, 0.1)',
    },
    deleteBtn: {
      backgroundColor: 'red',
      color: 'white',
      border: 'none',
      borderRadius: '3px',
      padding: '0.25rem 0.5rem',
      cursor: 'pointer',
    },
    addBtn: {
      color: 'white',
      border: 'none',
      borderRadius: '3px',
      padding: '0.25rem 0.5rem',
      cursor: 'pointer',
    },
    label: {
      marginRight: '1rem',
      fontSize: '0.85rem',
    }
  };

  return (
    <div>
      <h2>Correus amb accés a tots els formularis</h2>

      <div style={styles.container}>
        {mails.map(mail => (
          <div key={mail} style={styles.mail}>
            <div style={styles.label}>{mail}</div>
            <button
              style={styles.deleteBtn}
              onClick={() => socket.emit('.remove_default_mail', mail)}
            >
              Elimina
            </button>
          </div>
        ))}

        <button
          style={styles.addBtn}
          onClick={() => {
            const newMail = prompt('Introdueix el correu electrònic de la persona a afegir');
            if (newMail && isTextAMail(newMail)) {
              socket.emit('.add_default_mail', newMail);
            } else if (newMail) {
              alert('El correu introduït no és un correu vàlid');
            }
          }}
        >
          + Afegeix un correu
        </button>
      </div>
    </div>
  );
}

function FormsTable({ formsList, forms, socket, className }) {
  const [showMore, setShowMore] = useState(25);

  const thStyle = { padding: '8px 12px' };

  return (
    <>
      <table className={className} style={{ width: '100%', minWidth: 600, borderCollapse: 'collapse', marginTop: '0.5rem' }}>
        <thead>
          <tr style={{ borderBottom: '2px solid #ddd', textAlign: 'left' }}>
            <th style={thStyle}>Formulari</th>
            <th style={thStyle}>Assistència</th>
            <th style={{ ...thStyle, textAlign: 'center' }}>Respostes</th>
            <th style={{ ...thStyle, textAlign: 'right' }}>Ingressos</th>
          </tr>
        </thead>
        <tbody>
          {formsList.slice(0, showMore).map(eventInfo => (
            <FormInfoRow
              key={eventInfo.id}
              socket={socket}
              eventId={eventInfo.id}
              eventInfo={eventInfo}
              form={forms[eventInfo.id]}
            />
          ))}
        </tbody>
      </table>
      {showMore < formsList.length && (
        <div style={{ width: '100%', display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
          <button
            style={{ padding: '10px 20px' }}
            onClick={() => setShowMore(prev => prev + 25)}
          >
            Mostra'n més
          </button>
        </div>
      )}
    </>
  );
}

function Dashboard({ forms, setForms, socket, castellersInfo, events, setSelectedEvent }) {
  const allForms = Object.keys(forms)
    .map(eventId => events.find(event => event.id == eventId))
    .filter(eventInfo => eventInfo)

  const futureForms = allForms
    .filter(e => new Date(e['data-esperada-inici']) >= new Date())
    .sort((a, b) => new Date(a['data-esperada-inici']) - new Date(b['data-esperada-inici']))

  const pastForms = allForms
    .filter(e => new Date(e['data-esperada-inici']) < new Date())
    .sort((a, b) => new Date(b['data-esperada-inici']) - new Date(a['data-esperada-inici']))

  return (
    <div style={{ marginTop: '1rem' }}>
      <h2>Formularis actius</h2>
      {futureForms.length === 0 ? (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', fontWeight: '300', color: '#555' }}>
          No hi ha formularis actius.
        </div>
      ) : (
        <FormsTable formsList={futureForms} forms={forms} socket={socket} />
      )}

      <h2 style={{ marginTop: '2rem' }}>Formularis passats</h2>
      {pastForms.length === 0 ? (
        <div style={{ marginTop: '1rem', fontSize: '0.8rem', fontWeight: '300', color: '#555' }}>
          No hi ha formularis passats.
        </div>
      ) : (
        <FormsTable formsList={pastForms} forms={forms} socket={socket} className="forms-table-past" />
      )}
    </div>
  );
}

function FormsApp({ socket, userInfo, castellersInfo, setCastellersInfo, isPublic, serverId, ...props }) {
    const params = window.location.pathname.split('/').filter(part => part != '');
    const URLparsedSelectedEvent = parseInt(params[1]) || null;

    const dashboardTabNames = ['formularis', 'correus', 'ingressos'];
    const contentTabNames = ['recompte', 'individuals', 'editar', 'historial', 'comparteix'];

    const [events, setEvents] = useState([])
    const [selectedEvent, setSelectedEvent] = useState(URLparsedSelectedEvent)
    const [selectedDay, setSelectedDay] = useState(null)
    const [notification, setNotification] = useState(null);
    const [preview, setPreview] = useState(false);
    const [showPast, setShowPast] = useState(false);
    const [forms, setForms] = useState({});
    const [showShamelessPlug, setShowShamelessPlug] = useState(false);
    const [currentURL, setCurrentURL] = useState(window.location.href);
    const [dashboardTab, setDashboardTab] = useState(
        !URLparsedSelectedEvent && dashboardTabNames.includes(params[1]) ? params[1] : 'formularis'
    );
    const [contentTab, setContentTab] = useState(
        URLparsedSelectedEvent && contentTabNames.includes(params[2]) ? params[2] : 'recompte'
    );

    useEffect(() => {
      const visited = localStorage.getItem('fesmelacta_visited');
      if (visited) {
        setShowShamelessPlug(false);
      } else {
        setShowShamelessPlug(true);
      }
    }, [])

    const hidePlug = () => {
      setShowShamelessPlug(false);
      localStorage.setItem('fesmelacta_visited', 1);
    }

    // Sync URL with tab/event state
    useEffect(() => {
        if (window.location.href.includes('/respostes')) return;

        const path = selectedEvent
            ? (contentTab !== 'recompte' ? `/forms/${selectedEvent}/${contentTab}` : `/forms/${selectedEvent}`)
            : (dashboardTab !== 'formularis' ? `/forms/${dashboardTab}` : '/forms');

        if (window.location.pathname === path) return;

        window.history.pushState({}, '', path);
        setCurrentURL(window.location.href);
    }, [selectedEvent, dashboardTab, contentTab]);

    // Handle browser back/forward
    useEffect(() => {
        const handlePopState = () => {
            const p = window.location.pathname.split('/').filter(part => part != '');
            const eventId = parseInt(p[1]) || null;

            setSelectedEvent(eventId);
            if (eventId) {
                setContentTab(contentTabNames.includes(p[2]) ? p[2] : 'recompte');
            } else {
                setDashboardTab(dashboardTabNames.includes(p[1]) ? p[1] : 'formularis');
            }
            setCurrentURL(window.location.href);
        };

        window.addEventListener('popstate', handlePopState);
        return () => window.removeEventListener('popstate', handlePopState);
    }, []);

    const handleSelectEvent = (eventId) => {
        setSelectedEvent(eventId);
        if (eventId) setContentTab('recompte');
    };

    useEffect(() => {
      fetchAPI(`/get_all_forms`, setForms);
    }, []);

    // Preload assistencies and form responses for the selected event
    // so NotifyAlreadyVenen can render immediately without a layout jump
    const [preloadedAssistencies, setPreloadedAssistencies] = useState(null);
    const [preloadedFormResponseUserIds, setPreloadedFormResponseUserIds] = useState(null);

    useEffect(() => {
      setPreloadedAssistencies(null);
      setPreloadedFormResponseUserIds(null);

      if (selectedEvent) {
        fetchAPI(`/assistencies_event/${selectedEvent}`, data => {
          setPreloadedAssistencies(data.data);
        });
        fetchAPI(`/form_responses/${selectedEvent}`, data => {
          if (data.evId == selectedEvent) {
            setPreloadedFormResponseUserIds(Object.keys(data.responses));
          }
        });
      }
    }, [selectedEvent]);

    useEffect(() => {
      if (preloadedFormResponseUserIds && preloadedFormResponseUserIds.length === 0 && contentTab === 'recompte') {
        setContentTab('editar');
      }
    }, [preloadedFormResponseUserIds]);

    useEffect(() => {
      document.title = `Formularis - Aleta`;
    }, []);

    return (
        <div
            style={{ width: '90%', boxSizing: 'border-box', paddingTop: 50, paddingBottom: 50 }}
        >

          {
            isPublic && (
              <>
                <h1 style={{ marginTop: 20, marginBottom: 20 }}>
                  {forms?.[selectedEvent]?.title}
                </h1>
                {forms?.[selectedEvent]?.description && (
                  <p style={{ marginTop: -10, marginBottom: 30, color: '#666' }}>
                    {forms[selectedEvent].description}
                  </p>
                )}
                { selectedEvent && <FormResponses isPublic={isPublic} socket={socket} userInfo={userInfo} selectedEvent={selectedEvent} castellersInfo={castellersInfo} /> }
              </>
            )
          }

          {
            !isPublic && <>
              <UserInfo castellersInfo={castellersInfo} {...props} socket={socket} userInfo={userInfo} />

              <HeaderTabs userInfo={userInfo} />

              <Pressable style={{ backgroundColor: '#eee' }} className="boto-back" href='/'>
                  ← Tornar a la pàgina principal
              </Pressable>

              <Notifications msg={notification} setMsg={setNotification} />

              <h1>Formularis</h1>

              <div className="page-layout">
                <div className="choose-event-sidebar">
                  <div className="sidebar-desktop-only">
                    <div
                      className={`choose-event-item ${!selectedEvent && dashboardTab === 'formularis' ? 'active' : ''}`}
                      onClick={() => { setSelectedEvent(null); setSelectedDay(null); setDashboardTab('formularis'); }}
                    >
                      <strong><i className="fas fa-file-alt"></i> Formularis</strong>
                    </div>
                    <div
                      className={`choose-event-item ${!selectedEvent && dashboardTab === 'correus' ? 'active' : ''}`}
                      onClick={() => { setSelectedEvent(null); setSelectedDay(null); setDashboardTab('correus'); }}
                    >
                      <strong><i className="fas fa-envelope"></i> Correus amb accés</strong>
                    </div>
                    <div
                      className={`choose-event-item ${!selectedEvent && dashboardTab === 'ingressos' ? 'active' : ''}`}
                      onClick={() => { setSelectedEvent(null); setSelectedDay(null); setDashboardTab('ingressos'); }}
                    >
                      <strong><i className="fas fa-chart-bar"></i> Ingressos</strong>
                    </div>

                    <hr style={{ border: 'none', borderTop: '1px solid #e2e8f0', margin: '4px 0' }} />
                  </div>

                  <ChooseEvent forms={forms} showPast={showPast} setShowPast={setShowPast} events={events} setEvents={setEvents} selectedEvent={selectedEvent} setSelectedEvent={handleSelectEvent} setSelectedDay={setSelectedDay} socket={socket} />
                </div>

                <div className="page-content">
                  {
                    !selectedEvent && dashboardTab === 'formularis' && isBrowser && showShamelessPlug && (
                      <div style={{ backgroundColor: '#FFEEDD', padding: '5px 20px', borderRadius: 10, marginBottom: 20, position: 'relative' }}>
                        <p style={{ fontSize: 14, fontStyle: 'italic' }}>
                          Ets junta? Necessites fer la transcripció d'una acta o reunió? Prova <a target="__blank" href={`https://fesmelacta.cat?ref=alt&col=${COLLA.slice(0,3)}`}>fesmelacta.cat</a>!
                        </p>
                        <span style={{ position: 'absolute', top: '5px', right: '10px', cursor: 'pointer', color: '#777' }} onClick={hidePlug}>✖</span>
                      </div>
                    )
                  }

                  {selectedEvent && (
                    <div className="form-content-tabs">
                      <button className={`form-content-tab ${contentTab === 'recompte' ? 'active' : ''}`} onClick={() => setContentTab('recompte')}><i className="fas fa-chart-pie"></i> Recompte</button>
                      <button className={`form-content-tab ${contentTab === 'individuals' ? 'active' : ''}`} onClick={() => setContentTab('individuals')}><i className="fas fa-user"></i> Respostes individuals</button>
                      <button className={`form-content-tab ${contentTab === 'editar' ? 'active' : ''}`} onClick={() => setContentTab('editar')}><i className="fas fa-pen"></i> Editar formulari</button>
                      <button className={`form-content-tab ${contentTab === 'historial' ? 'active' : ''}`} onClick={() => setContentTab('historial')}><i className="fas fa-history"></i> Historial</button>
                      <button className={`form-content-tab ${contentTab === 'comparteix' ? 'active' : ''}`} onClick={() => setContentTab('comparteix')}><i className="fas fa-share-alt"></i> Comparteix</button>
                    </div>
                  )}

                  { selectedEvent && <FormResponses view={contentTab} isPublic={isPublic} socket={socket} userInfo={userInfo} selectedEvent={selectedEvent} castellersInfo={castellersInfo} /> }

                  { selectedEvent && contentTab === 'editar' && <FormEditor forms={forms} events={events} userInfo={userInfo} castellersInfo={castellersInfo} selectedDay={selectedDay} preview={preview} setPreview={setPreview} socket={socket} selectedEvent={selectedEvent} notification={notification} setNotification={setNotification} preloadedAssistencies={preloadedAssistencies} preloadedFormResponseUserIds={preloadedFormResponseUserIds} /> }

                  { selectedEvent && contentTab === 'historial' && <FormLogs socket={socket} selectedEvent={selectedEvent} castellersInfo={castellersInfo} userInfo={userInfo} /> }


                  { !selectedEvent && dashboardTab === 'formularis' && <Dashboard forms={forms} setForms={setForms} socket={socket} events={events} castellersInfo={castellersInfo} setSelectedEvent={setSelectedEvent} /> }
                  { !selectedEvent && dashboardTab === 'correus' && <CorreusSection socket={socket} /> }
                  { !selectedEvent && dashboardTab === 'ingressos' && <FormsStats forms={forms} /> }
                </div>
              </div>
            </>
          }

            <div style={{ height: 100 }}>&nbsp;</div>
        </div>
    )
}

export default FormsApp;